import { useState } from "react";
import { Loader2, X, Copy, Download, Instagram, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { adminApi, formatApiError } from "@/lib/adminApi";

const VARIANT_META = [
  { key: "square",     label: "Cuadrado 1:1",   sub: "Feed Instagram / Facebook" },
  { key: "vertical",   label: "Vertical 9:16",  sub: "Story / Reel / TikTok" },
  { key: "horizontal", label: "Horizontal 16:9", sub: "LinkedIn / Facebook landscape" },
];

function CopyButton({ text, label, testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copiado`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("No se pudo copiar");
    }
  };
  return (
    <button
      onClick={onCopy}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#2c4a3b] text-white hover:bg-[#1e3328]"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copiado" : `Copiar ${label}`}
    </button>
  );
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function SocialPostModal({ open, onClose, leadId, leadName }) {
  const [loading, setLoading] = useState(false);
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");
  const [activeVariant, setActiveVariant] = useState("square");
  const [activeLang, setActiveLang] = useState("es");

  const firstName = (leadName || "").split(" ")[0] || "lead";

  const generate = async () => {
    setLoading(true);
    setError("");
    setPost(null);
    try {
      const data = await adminApi.generateSocialPost(leadId);
      setPost(data);
      toast.success("Post generado");
    } catch (err) {
      setError(formatApiError(err));
      toast.error("No se pudo generar el post");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const caption = post ? (activeLang === "es" ? post.caption_es : post.caption_en) : "";
  const hashtagsStr = post ? (post.hashtags || []).join(" ") : "";
  const fullPost = post ? `${caption}\n\n${hashtagsStr}` : "";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
      onClick={onClose}
      data-testid="social-modal"
    >
      <div
        className="bg-white rounded-3xl max-w-4xl w-full my-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-7 py-5 border-b border-[#2c4a3b]/8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#bc6c25] to-[#9c581e] grid place-items-center text-white">
              <Instagram className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-[#1a2421] leading-tight">Generador de post IG</h2>
              <p className="text-xs text-[#5c6b61]">Inspirado en {firstName} · Imagen IA + captions ES/EN + hashtags</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-social-modal"
            className="w-9 h-9 rounded-full grid place-items-center text-[#5c6b61] hover:bg-[#f9f6f0]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-7">
          {!post && !loading && (
            <div className="text-center py-10">
              <p className="text-[#5c6b61] mb-6 max-w-md mx-auto">
                Voy a crear una imagen estilo Eliora con IA, un caption en español + inglés,
                y 18-22 hashtags optimizados — todo basado en el protocolo de <strong>{firstName}</strong>.
                <br/><br/>
                <span className="text-xs">⏱️ Tarda ~20 segundos.</span>
              </p>
              <Button
                onClick={generate}
                data-testid="generate-social-post-btn"
                className="rounded-full bg-[#bc6c25] hover:bg-[#9c581e] text-white px-7 py-6 text-sm font-semibold shadow-lg shadow-[#bc6c25]/20"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generar post con IA
              </Button>
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-16 text-[#5c6b61]">
              <Loader2 className="w-10 h-10 animate-spin text-[#bc6c25]" />
              <p className="mt-4 text-sm">Generando imagen y captions… (~20s)</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-sm mb-4">
              {error}
            </div>
          )}

          {post && !loading && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* ----- LEFT: image variants ----- */}
              <div>
                <div className="flex gap-2 mb-3 overflow-x-auto">
                  {VARIANT_META.map((v) => (
                    <button
                      key={v.key}
                      onClick={() => setActiveVariant(v.key)}
                      data-testid={`variant-${v.key}-tab`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                        activeVariant === v.key
                          ? "bg-[#2c4a3b] text-white"
                          : "bg-[#f9f6f0] text-[#5c6b61] hover:bg-[#f0ebe1]"
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>

                <div className="bg-[#f9f6f0] rounded-2xl p-4 flex items-center justify-center min-h-[280px]">
                  <img
                    src={post.image_variants[activeVariant]}
                    alt={`Post ${activeVariant}`}
                    className="max-w-full max-h-[420px] rounded-xl shadow-lg"
                    data-testid={`variant-${activeVariant}-img`}
                  />
                </div>

                <p className="text-xs text-[#5c6b61] mt-3 text-center">
                  {VARIANT_META.find((v) => v.key === activeVariant)?.sub}
                </p>

                <Button
                  onClick={() =>
                    downloadDataUrl(
                      post.image_variants[activeVariant],
                      `eliora-ig-${activeVariant}-${firstName}.jpg`
                    )
                  }
                  data-testid="download-image-btn"
                  variant="outline"
                  className="w-full mt-3 rounded-full border-[#2c4a3b]/30"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar imagen {activeVariant}
                </Button>
              </div>

              {/* ----- RIGHT: captions + hashtags ----- */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-1 bg-[#f9f6f0] rounded-full p-1">
                    <button
                      onClick={() => setActiveLang("es")}
                      data-testid="lang-es-tab"
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
                        activeLang === "es" ? "bg-[#2c4a3b] text-white" : "text-[#5c6b61]"
                      }`}
                    >
                      ES
                    </button>
                    <button
                      onClick={() => setActiveLang("en")}
                      data-testid="lang-en-tab"
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
                        activeLang === "en" ? "bg-[#2c4a3b] text-white" : "text-[#5c6b61]"
                      }`}
                    >
                      EN
                    </button>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#bc6c25] font-semibold">
                    Tono · {post.tone_chosen}
                  </span>
                </div>

                <div className="bg-[#f9f6f0] rounded-2xl p-5">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#5c6b61] mb-2">Caption</p>
                  <p
                    className="text-sm text-[#1a2421] whitespace-pre-line leading-relaxed"
                    data-testid={`caption-${activeLang}`}
                  >
                    {caption}
                  </p>
                </div>

                <div className="mt-4 bg-[#f9f6f0] rounded-2xl p-5">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#5c6b61] mb-2">
                    Hashtags ({(post.hashtags || []).length})
                  </p>
                  <p className="text-sm text-[#2c4a3b] leading-relaxed break-words" data-testid="hashtags-block">
                    {hashtagsStr}
                  </p>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <CopyButton text={caption} label="caption" testId="copy-caption-btn" />
                  <CopyButton text={hashtagsStr} label="hashtags" testId="copy-hashtags-btn" />
                  <CopyButton text={fullPost} label="todo" testId="copy-all-btn" />
                </div>

                <Button
                  onClick={generate}
                  variant="outline"
                  className="w-full mt-5 rounded-full border-[#bc6c25]/40 text-[#9c581e] hover:bg-[#bc6c25]/5"
                  data-testid="regenerate-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Regenerar (otra versión)
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
